import React, { useCallback, useEffect, useState, useRef, createContext, useContext } from 'react';
import { Visit, Client, User, GPSLocation, Role, Permissions, Order, OrderLine, Product } from '../types';
import { supabaseAdmin } from '../lib/supabase';
import { useAuth } from './AuthContext';
// ── Column selections to reduce egress ───────────────────────────────────────
const VISIT_COLUMNS = 'id, vendedor_id, vendedor_nombre, cliente_id, cliente_nombre, zona, tipo_visita, fecha, hora_llegada, hora_salida, tiempo_total, ubicacion_llegada, ubicacion_salida, cobro, venta, comentarios, motivo_no_pago, productos_vencidos, devolucion_items, proxima_visita, estado, numero_recibo, created_at';
const CLIENT_COLUMNS = 'id, codigo, nombre, telefono, ciudad, limite, cedula, rnc, ruta, direccion, vendedor_id, vendedor_nombre, dependencia, zona, lat, lng';
const PROFILE_COLUMNS = 'id, nombre, email, password, rol_id, activo, avatar_url, fecha_creacion';
const ORDER_COLUMNS = 'id, numero_pedido, farmacia, vendedor, fecha, items, total, estado, order_lines(producto_id, nombre, sku, cantidad, precio, stock_disponible)';
interface AppContextType {
  visits: Visit[];
  clients: Client[];
  users: User[];
  roles: Role[];
  orders: Order[];
  products: Product[];
  isLoading: boolean;
  newOrderAlert: boolean;
  clearNewOrderAlert: () => void;
  addVisit: (visit: Omit<Visit, 'id'>) => Promise<void>;
  updateVisit: (id: string, updates: Partial<Visit>) => Promise<void>;
  startVisit: (vendedorId: string, vendedorNombre: string, clienteId: string, tipoVisita: Visit['tipoVisita']) => Visit;
  endVisit: (visitId: string, visitData: Partial<Visit>) => Promise<void>;
  getVisitsByVendedor: (vendedorId: string) => Visit[];
  getVisitsByDate: (date: Date) => Visit[];
  getTodayStats: (vendedorId?: string) => {
    totalVisitas: number;
    totalCobrado: number;
    totalVendido: number;
    tiempoPromedio: number;
    visitasCompletadas: number;
    visitasEnProgreso: number;
  };
  addClient: (client: Omit<Client, 'id'>) => Promise<{
    success: boolean;
    error?: string;
  }>;
  updateClient: (id: string, updates: Partial<Client>) => Promise<{
    success: boolean;
    error?: string;
  }>;
  deleteClient: (id: string) => Promise<{
    success: boolean;
    error?: string;
  }>;
  addUser: (user: Omit<User, 'id' | 'fechaCreacion' | 'rolNombre' | 'permisos'>) => Promise<{
    success: boolean;
    error?: string;
  }>;
  updateUser: (id: string, updates: Partial<User>) => Promise<{
    success: boolean;
    error?: string;
  }>;
  deleteUser: (id: string) => Promise<{
    success: boolean;
    error?: string;
  }>;
  addRole: (role: Omit<Role, 'id' | 'fechaCreacion'>) => Promise<{
    success: boolean;
    error?: string;
  }>;
  updateRole: (id: string, updates: Partial<Role>) => Promise<{
    success: boolean;
    error?: string;
  }>;
  deleteRole: (id: string) => Promise<{
    success: boolean;
    error?: string;
  }>;
  updateOrderStatus: (orderId: string, newStatus: Order['estado']) => Promise<void>;
  getCurrentLocation: () => Promise<GPSLocation | null>;
  refreshData: () => Promise<void>;
  loadProducts: () => Promise<void>;
}
const AppContext = createContext<AppContextType | undefined>(undefined);
const DR_CENTER = {
  lat: 18.4861,
  lng: -69.9312
};
function mapVisit(row: any): Visit {
  return {
    id: row.id,
    vendedorId: row.vendedor_id,
    vendedorNombre: row.vendedor_nombre,
    clienteId: row.cliente_id,
    clienteNombre: row.cliente_nombre,
    zona: row.zona,
    tipoVisita: row.tipo_visita,
    fecha: new Date(row.fecha),
    horaLlegada: row.hora_llegada ? new Date(row.hora_llegada) : null,
    horaSalida: row.hora_salida ? new Date(row.hora_salida) : null,
    tiempoTotal: row.tiempo_total,
    ubicacionLlegada: row.ubicacion_llegada ? {
      ...row.ubicacion_llegada,
      timestamp: new Date(row.ubicacion_llegada.timestamp)
    } : null,
    ubicacionSalida: row.ubicacion_salida ? {
      ...row.ubicacion_salida,
      timestamp: new Date(row.ubicacion_salida.timestamp)
    } : null,
    cobro: {
      huboCobo: false,
      montoCobrado: 0,
      metodoPago: null,
      numeroFactura: '',
      estadoFactura: 'no_pago',
      credito: 0,
      descuento: 0,
      ...(row.cobro || {})
    },
    venta: {
      huboVenta: false,
      montoVenta: 0,
      numeroPedido: '',
      ...(row.venta || {})
    },
    comentarios: row.comentarios || '',
    motivoNoPago: row.motivo_no_pago || '',
    productosVencidos: row.productos_vencidos ?? null,
    devolucionItems: row.devolucion_items || [],
    proximaVisita: row.proxima_visita ? new Date(row.proxima_visita) : null,
    estado: row.estado,
    numeroRecibo: row.numero_recibo || null
  };
}
function mapClient(row: any): Client {
  return {
    id: row.id,
    codigo: row.codigo || '',
    nombre: row.nombre,
    telefono: row.telefono || '',
    ciudad: row.ciudad || '',
    limite: row.limite || 0,
    cedula: row.cedula || '',
    rnc: row.rnc || '',
    ruta: row.ruta || '',
    direccion: row.direccion,
    vendedorId: row.vendedor_id || undefined,
    vendedorNombre: row.vendedor_nombre || undefined,
    dependencia: row.dependencia || '',
    zona: row.zona || '',
    lat: row.lat,
    lng: row.lng
  };
}
function mapRole(row: any): Role {
  return {
    id: row.id,
    nombre: row.nombre,
    permisos: row.permisos as Permissions,
    fechaCreacion: new Date(row.created_at)
  };
}
function mapUser(row: any, roleMap: Record<string, Role>): User {
  const role = roleMap[row.rol_id] || {
    nombre: 'Sin Rol',
    permisos: {}
  };
  return {
    id: row.id,
    nombre: row.nombre,
    email: row.email || '',
    password: row.password,
    rolId: row.rol_id,
    rolNombre: role.nombre,
    permisos: role.permisos,
    activo: row.activo,
    avatarUrl: row.avatar_url || '',
    fechaCreacion: new Date(row.fecha_creacion)
  };
}
function mapOrder(row: any): Order {
  return {
    id: row.id,
    numeroPedido: row.numero_pedido,
    farmacia: row.farmacia,
    vendedor: row.vendedor,
    fecha: new Date(row.fecha),
    items: row.items,
    total: row.total,
    estado: row.estado,
    lineas: (row.order_lines || []).map((l: any): OrderLine => ({
      productoId: l.producto_id,
      nombre: l.nombre,
      sku: l.sku,
      cantidad: l.cantidad,
      precio: l.precio,
      stockDisponible: l.stock_disponible
    }))
  };
}
// ── Map product row ──────────────────────────────────────────────────────────
function mapProduct(row: any): Product {
  return {
    id: row.id,
    nombre: row.nombre || '',
    sku: row.sku || '',
    stock: row.stock ?? 0,
    stockMinimo: row.stock_minimo ?? 0,
    precio: row.precio ?? 0,
    subTotal: row.sub_total ?? row.precio ?? 0,
    itbis: row.itbis ?? 0,
    proveedor: row.proveedor ?? '',
    estado: row.estado || 'activo'
  };
}
// ── Build a WAV Blob for a single sine-wave beep ─────────────────────────────
function makeBeepBlob(freq: number, durationSec: number): Blob {
  const sr = 44100;
  const n = Math.floor(sr * durationSec);
  const buf = new ArrayBuffer(44 + n * 2);
  const v = new DataView(buf);
  const s = (o: number, str: string) => {
    for (let i = 0; i < str.length; i++) v.setUint8(o + i, str.charCodeAt(i));
  };
  s(0, 'RIFF');
  v.setUint32(4, 36 + n * 2, true);
  s(8, 'WAVE');
  s(12, 'fmt ');
  v.setUint32(16, 16, true);
  v.setUint16(20, 1, true);
  v.setUint16(22, 1, true);
  v.setUint32(24, sr, true);
  v.setUint32(28, sr * 2, true);
  v.setUint16(32, 2, true);
  v.setUint16(34, 16, true);
  s(36, 'data');
  v.setUint32(40, n * 2, true);
  for (let i = 0; i < n; i++) {
    const t = i / sr;
    const env = t < 0.005 ? t / 0.005 : t > durationSec - 0.01 ? (durationSec - t) / 0.01 : 1;
    v.setInt16(44 + i * 2, Math.round(Math.sin(2 * Math.PI * freq * t) * env * 32000), true);
  }
  return new Blob([buf], {
    type: 'audio/wav'
  });
}
// ── New-order alert: pleasant store-bell chime ───────────────────────────
// Two-tone ascending chime that sounds like a doorbell / cash register
function playNewOrderAlert() {
  try {
    const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
    // Chime pattern: two bright ascending tones with harmonics
    const chimes = [{
      freq: 880,
      start: 0,
      dur: 0.35
    }, {
      freq: 1108.73,
      start: 0.18,
      dur: 0.35
    }, {
      freq: 1318.51,
      start: 0.36,
      dur: 0.5
    } // E6 — sustained
    ];
    chimes.forEach(({
      freq,
      start,
      dur
    }) => {
      // Main tone
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0, ctx.currentTime + start);
      gain.gain.linearRampToValueAtTime(0.5, ctx.currentTime + start + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + start + dur);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(ctx.currentTime + start);
      osc.stop(ctx.currentTime + start + dur + 0.05);
      // Soft octave harmonic for richness
      const osc2 = ctx.createOscillator();
      const gain2 = ctx.createGain();
      osc2.type = 'sine';
      osc2.frequency.value = freq * 2;
      gain2.gain.setValueAtTime(0, ctx.currentTime + start);
      gain2.gain.linearRampToValueAtTime(0.12, ctx.currentTime + start + 0.01);
      gain2.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + start + dur * 0.7);
      osc2.connect(gain2);
      gain2.connect(ctx.destination);
      osc2.start(ctx.currentTime + start);
      osc2.stop(ctx.currentTime + start + dur + 0.05);
    });
    // Second ring after a short pause (like a real bell bouncing)
    setTimeout(() => {
      try {
        const ctx2 = new (window.AudioContext || (window as any).webkitAudioContext)();
        const osc = ctx2.createOscillator();
        const gain = ctx2.createGain();
        osc.type = 'sine';
        osc.frequency.value = 1318.51; // E6
        gain.gain.setValueAtTime(0, ctx2.currentTime);
        gain.gain.linearRampToValueAtTime(0.3, ctx2.currentTime + 0.01);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx2.currentTime + 0.6);
        osc.connect(gain);
        gain.connect(ctx2.destination);
        osc.start(ctx2.currentTime);
        osc.stop(ctx2.currentTime + 0.65);
      } catch (_) {}
    }, 700);
  } catch (_) {}
}
export function AppProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const {
    user: authUser
  } = useAuth();
  const [visits, setVisits] = useState<Visit[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [newOrderAlert, setNewOrderAlert] = useState(false);
  const clearNewOrderAlert = () => setNewOrderAlert(false);
  // ── Refs for smart refresh ─────────────────────────────────────────────────
  const isLoadingRef = useRef(false);
  const isInitialLoadRef = useRef(true);
  const lastLoadTimestampRef = useRef(0);
  const productsLoadedRef = useRef(false);
  const MIN_RELOAD_INTERVAL = 60000; // 60s mínimo entre reloads completos (was 15s)
  // ── Track pending orders count to detect new arrivals globally ─────────
  const LAST_PENDING_KEY = 'mr_last_pending_count';
  const pendingCountRef = useRef<number>(-1);
  // ── Track empacado orders for repartidor role ─────────────────────────
  const LAST_EMPACADO_KEY = 'mr_last_empacado_count';
  const empacadoCountRef = useRef<number>(-1);
  const isRepartidor = authUser?.permisos?.dashboard_entregas === true && authUser?.rolNombre !== 'Administrador';
  const getCurrentLocation = (): Promise<GPSLocation | null> => {
    return new Promise(resolve => {
      if (!navigator.geolocation) {
        resolve(null);
        return;
      }
      navigator.geolocation.getCurrentPosition(pos => resolve({
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
        timestamp: new Date()
      }), () => resolve(null), {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      });
    });
  };
  const loadData = async (showSpinner = false) => {
    // Prevent concurrent loads
    if (isLoadingRef.current) return;
    const now = Date.now();
    if (now - lastLoadTimestampRef.current < MIN_RELOAD_INTERVAL && !showSpinner) return;
    isLoadingRef.current = true;
    lastLoadTimestampRef.current = now;
    if (showSpinner || isInitialLoadRef.current) {
      setIsLoading(true);
    }
    try {
      const [visitsRes, clientsRes, rolesRes, profilesRes, ordersRes] = await Promise.all([supabaseAdmin.from('visits').select(VISIT_COLUMNS).order('created_at', {
        ascending: false
      }).limit(300), supabaseAdmin.from('clients').select(CLIENT_COLUMNS).order('nombre'), supabaseAdmin.from('roles').select('*').order('nombre'), supabaseAdmin.from('profiles').select(PROFILE_COLUMNS).order('nombre'), supabaseAdmin.from('orders').select(ORDER_COLUMNS).order('fecha', {
        ascending: false
      }).limit(200)]);
      if (visitsRes.data) setVisits(visitsRes.data.map(mapVisit));
      if (clientsRes.data) setClients(clientsRes.data.map(mapClient));
      let loadedRoles: Role[] = [];
      if (rolesRes.data) {
        loadedRoles = rolesRes.data.map(mapRole);
        setRoles(loadedRoles);
      }
      if (profilesRes.data) {
        const roleMap: Record<string, Role> = {};
        loadedRoles.forEach(r => roleMap[r.id] = r);
        setUsers(profilesRes.data.map(p => mapUser(p, roleMap)));
      }
      if (ordersRes.data) setOrders(ordersRes.data.map(mapOrder));
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      isLoadingRef.current = false;
      isInitialLoadRef.current = false;
      setIsLoading(false);
    }
  };
  // ── Lazy product loader (shared across all pages) ──────────────────────────
  const loadProducts = useCallback(async () => {
    if (productsLoadedRef.current && products.length > 0) return;
    try {
      const allProducts: Product[] = [];
      const PAGE_SIZE = 1000;
      let from = 0;
      let hasMore = true;
      while (hasMore) {
        const {
          data,
          error
        } = await supabaseAdmin.from('products').select('id, nombre, sku, stock, stock_minimo, precio, sub_total, itbis, proveedor, estado').order('nombre').range(from, from + PAGE_SIZE - 1);
        if (error) break;
        if (data && data.length > 0) {
          allProducts.push(...data.map(mapProduct));
          from += PAGE_SIZE;
          hasMore = data.length === PAGE_SIZE;
        } else {
          hasMore = false;
        }
      }
      setProducts(allProducts);
      productsLoadedRef.current = true;
    } catch (err) {
      console.error('Error loading products:', err);
    }
  }, [products.length]);
  // ── Individual table refreshers (for targeted realtime updates) ────────────
  const refreshVisits = async () => {
    const {
      data
    } = await supabaseAdmin.from('visits').select(VISIT_COLUMNS).order('created_at', {
      ascending: false
    }).limit(300);
    if (data) setVisits(data.map(mapVisit));
  };
  const refreshClients = async () => {
    const {
      data
    } = await supabaseAdmin.from('clients').select(CLIENT_COLUMNS).order('nombre');
    if (data) setClients(data.map(mapClient));
  };
  const refreshRolesAndUsers = async () => {
    const [rolesRes, profilesRes] = await Promise.all([supabaseAdmin.from('roles').select('*').order('nombre'), supabaseAdmin.from('profiles').select(PROFILE_COLUMNS).order('nombre')]);
    let loadedRoles: Role[] = [];
    if (rolesRes.data) {
      loadedRoles = rolesRes.data.map(mapRole);
      setRoles(loadedRoles);
    }
    if (profilesRes.data) {
      const roleMap: Record<string, Role> = {};
      loadedRoles.forEach(r => roleMap[r.id] = r);
      setUsers(profilesRes.data.map(p => mapUser(p, roleMap)));
    }
  };
  const refreshOrders = async () => {
    const {
      data
    } = await supabaseAdmin.from('orders').select(ORDER_COLUMNS).order('fecha', {
      ascending: false
    }).limit(200);
    if (data) setOrders(data.map(mapOrder));
  };
  useEffect(() => {
    // Initial load with spinner
    loadData(true);
    // ── Realtime subscriptions (targeted refreshes) ──────────────────────────
    const logSub = (name: string) => (status: string) => {
      console.log(`[Realtime] ${name}: ${status}`);
    };
    const subVisits = supabaseAdmin.channel('visits-ch').on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: 'visits'
    }, payload => {
      console.log('[Realtime] visits change:', payload.eventType);
      if (payload.eventType === 'INSERT' && payload.new) {
        setVisits(prev => [mapVisit(payload.new), ...prev].slice(0, 300));
      } else if (payload.eventType === 'UPDATE' && payload.new) {
        setVisits(prev => prev.map(v => v.id === payload.new.id ? mapVisit(payload.new) : v));
      } else if (payload.eventType === 'DELETE' && payload.old) {
        setVisits(prev => prev.filter(v => v.id !== payload.old.id));
      } else {
        refreshVisits();
      }
    }).subscribe(logSub('visits'));
    const subRoles = supabaseAdmin.channel('roles-ch').on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: 'roles'
    }, payload => {
      console.log('[Realtime] roles change:', payload.eventType);
      refreshRolesAndUsers();
      window.dispatchEvent(new CustomEvent('supabase-change', {
        detail: {
          table: 'roles',
          eventType: payload.eventType
        }
      }));
    }).subscribe(logSub('roles'));
    const subProfiles = supabaseAdmin.channel('profiles-ch').on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: 'profiles'
    }, payload => {
      console.log('[Realtime] profiles change:', payload.eventType);
      refreshRolesAndUsers();
      window.dispatchEvent(new CustomEvent('supabase-change', {
        detail: {
          table: 'profiles',
          eventType: payload.eventType
        }
      }));
    }).subscribe(logSub('profiles'));
    const subClients = supabaseAdmin.channel('clients-ch').on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: 'clients'
    }, payload => {
      console.log('[Realtime] clients change:', payload.eventType);
      if (payload.eventType === 'INSERT' && payload.new) {
        setClients(prev => [...prev, mapClient(payload.new)].sort((a, b) => a.nombre.localeCompare(b.nombre)));
      } else if (payload.eventType === 'UPDATE' && payload.new) {
        setClients(prev => prev.map(c => c.id === payload.new.id ? mapClient(payload.new) : c));
      } else if (payload.eventType === 'DELETE' && payload.old) {
        setClients(prev => prev.filter(c => c.id !== payload.old.id));
      } else {
        refreshClients();
      }
    }).subscribe(logSub('clients'));
    // ── Orders: only subscribe to orders table (removed order_lines channel) ─
    const subOrders = supabaseAdmin.channel('orders-ch').on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: 'orders'
    }, payload => {
      console.log('[Realtime] orders change:', payload.eventType);
      if (payload.eventType === 'UPDATE' && payload.new) {
        setOrders(prev => prev.map(o => o.id === payload.new.id ? {
          ...o,
          ...mapOrder({
            ...payload.new,
            order_lines: o.lineas?.map(l => ({
              producto_id: l.productoId,
              nombre: l.nombre,
              sku: l.sku,
              cantidad: l.cantidad,
              precio: l.precio,
              stock_disponible: l.stockDisponible
            })) || []
          })
        } : o));
      } else {
        refreshOrders();
      }
    }).subscribe(logSub('orders'));
    // ── NO order_lines channel — saves 1 realtime connection + prevents ──────
    // ── unnecessary full order refreshes on every line change ─────────────────
    // ── Polling fallback — very infrequent since realtime handles updates ────
    const pollInterval = setInterval(() => {
      loadData();
    }, 3600000); // 60 min (was 10 min) — realtime handles live updates
    // ── Refresh on window focus (only if tab was hidden for > 5 min) ────────
    let lastHiddenAt = 0;
    const handleVisibility = () => {
      if (document.hidden) {
        lastHiddenAt = Date.now();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    const handleFocus = () => {
      if (lastHiddenAt > 0 && Date.now() - lastHiddenAt > 300000) {
        loadData();
      }
    };
    window.addEventListener('focus', handleFocus);
    const handleOnline = () => {
      console.log('[Network] Back online — refreshing data');
      loadData();
    };
    window.addEventListener('online', handleOnline);
    return () => {
      supabaseAdmin.removeChannel(subVisits);
      supabaseAdmin.removeChannel(subRoles);
      supabaseAdmin.removeChannel(subProfiles);
      supabaseAdmin.removeChannel(subClients);
      supabaseAdmin.removeChannel(subOrders);
      clearInterval(pollInterval);
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('online', handleOnline);
    };
  }, []);
  // ── Global pending-order monitor: plays sound on ANY page ──────────────
  const isCliente = authUser?.rolNombre === 'Cliente';
  useEffect(() => {
    // Repartidor uses empacado monitor instead
    if (isRepartidor) return;
    // Cliente role should NOT hear order sounds
    if (isCliente) return;
    const pendingOrders = orders.filter(o => o.estado === 'pendiente');
    const currentPendingCount = pendingOrders.length;
    const lastSeen = parseInt(localStorage.getItem(LAST_PENDING_KEY) || '-1', 10);
    if (lastSeen === -1) {
      // First load — just record current count, don't play sound
      localStorage.setItem(LAST_PENDING_KEY, String(currentPendingCount));
      pendingCountRef.current = currentPendingCount;
    } else if (currentPendingCount > lastSeen) {
      // New pending orders detected — play alert sound
      playNewOrderAlert();
      setNewOrderAlert(true);
      localStorage.setItem(LAST_PENDING_KEY, String(currentPendingCount));
      pendingCountRef.current = currentPendingCount;
    } else {
      // Count decreased or unchanged — just update storage
      if (currentPendingCount !== lastSeen) {
        localStorage.setItem(LAST_PENDING_KEY, String(currentPendingCount));
      }
      pendingCountRef.current = currentPendingCount;
    }
  }, [orders]);
  // ── Repartidor monitor: plays sound when orders become "empacado" ──────
  useEffect(() => {
    if (!isRepartidor) return;
    const empacadoOrders = orders.filter(o => o.estado === 'empacado');
    const currentEmpacadoCount = empacadoOrders.length;
    const lastSeen = parseInt(localStorage.getItem(LAST_EMPACADO_KEY) || '-1', 10);
    if (lastSeen === -1) {
      localStorage.setItem(LAST_EMPACADO_KEY, String(currentEmpacadoCount));
      empacadoCountRef.current = currentEmpacadoCount;
    } else if (currentEmpacadoCount > lastSeen) {
      playNewOrderAlert();
      setNewOrderAlert(true);
      localStorage.setItem(LAST_EMPACADO_KEY, String(currentEmpacadoCount));
      empacadoCountRef.current = currentEmpacadoCount;
    } else {
      if (currentEmpacadoCount !== lastSeen) {
        localStorage.setItem(LAST_EMPACADO_KEY, String(currentEmpacadoCount));
      }
      empacadoCountRef.current = currentEmpacadoCount;
    }
  }, [orders, isRepartidor]);
  const addVisit = useCallback(async (visit: Omit<Visit, 'id'>) => {
    const {
      data
    } = await supabaseAdmin.from('visits').insert({
      vendedor_id: visit.vendedorId,
      vendedor_nombre: visit.vendedorNombre,
      cliente_id: visit.clienteId,
      cliente_nombre: visit.clienteNombre,
      zona: visit.zona,
      tipo_visita: visit.tipoVisita,
      fecha: visit.fecha.toISOString(),
      hora_llegada: visit.horaLlegada?.toISOString() || null,
      hora_salida: visit.horaSalida?.toISOString() || null,
      tiempo_total: visit.tiempoTotal,
      ubicacion_llegada: visit.ubicacionLlegada ? {
        ...visit.ubicacionLlegada,
        timestamp: visit.ubicacionLlegada.timestamp.toISOString()
      } : null,
      ubicacion_salida: visit.ubicacionSalida ? {
        ...visit.ubicacionSalida,
        timestamp: visit.ubicacionSalida.timestamp.toISOString()
      } : null,
      cobro: visit.cobro,
      venta: visit.venta,
      comentarios: visit.comentarios,
      motivo_no_pago: visit.motivoNoPago,
      proxima_visita: visit.proximaVisita?.toISOString() || null,
      estado: visit.estado
    }).select().single();
    if (data) setVisits(prev => [mapVisit(data), ...prev]);
  }, []);
  const updateVisit = useCallback(async (id: string, updates: Partial<Visit>) => {
    const finalUpdates: any = {};
    if (updates.cobro) finalUpdates.cobro = updates.cobro;
    if (updates.venta) finalUpdates.venta = updates.venta;
    if (updates.comentarios !== undefined) finalUpdates.comentarios = updates.comentarios;
    if (updates.motivoNoPago !== undefined) finalUpdates.motivo_no_pago = updates.motivoNoPago;
    if (updates.estado) finalUpdates.estado = updates.estado;
    if (updates.horaSalida) finalUpdates.hora_salida = updates.horaSalida.toISOString();
    if (updates.tiempoTotal !== undefined) finalUpdates.tiempo_total = updates.tiempoTotal;
    if (updates.ubicacionSalida) finalUpdates.ubicacion_salida = {
      ...updates.ubicacionSalida,
      timestamp: updates.ubicacionSalida.timestamp.toISOString()
    };
    const {
      data
    } = await supabaseAdmin.from('visits').update(finalUpdates).eq('id', id).select().single();
    if (data) setVisits(prev => prev.map(v => v.id === id ? mapVisit(data) : v));
  }, []);
  const startVisit = useCallback((vendedorId: string, vendedorNombre: string, clienteId: string, tipoVisita: Visit['tipoVisita']): Visit => {
    const client = clients.find(c => c.id === clienteId);
    const tempId = `temp-${Date.now()}`;
    const newVisit: Visit = {
      id: tempId,
      vendedorId,
      vendedorNombre,
      clienteId,
      clienteNombre: client?.nombre || '',
      zona: client?.zona || '',
      tipoVisita,
      fecha: new Date(),
      horaLlegada: new Date(),
      horaSalida: null,
      tiempoTotal: null,
      ubicacionLlegada: null,
      ubicacionSalida: null,
      cobro: {
        huboCobo: false,
        montoCobrado: 0,
        metodoPago: null,
        numeroFactura: '',
        estadoFactura: 'no_pago',
        credito: 0,
        descuento: 0
      },
      venta: {
        huboVenta: false,
        montoVenta: 0,
        numeroPedido: ''
      },
      comentarios: '',
      motivoNoPago: '',
      proximaVisita: null,
      estado: 'en_progreso'
    };
    setVisits(prev => [newVisit, ...prev]);
    const save = async () => {
      const location = await getCurrentLocation();
      const {
        data
      } = await supabaseAdmin.from('visits').insert({
        vendedor_id: vendedorId,
        vendedor_nombre: vendedorNombre,
        cliente_id: clienteId,
        cliente_nombre: client?.nombre || '',
        zona: client?.zona || '',
        tipo_visita: tipoVisita,
        fecha: new Date().toISOString(),
        hora_llegada: new Date().toISOString(),
        ubicacion_llegada: location ? {
          lat: location.lat,
          lng: location.lng,
          timestamp: location.timestamp.toISOString()
        } : null,
        cobro: newVisit.cobro,
        venta: newVisit.venta,
        estado: 'en_progreso'
      }).select().single();
      if (data) setVisits(prev => prev.map(v => v.id === tempId ? mapVisit(data) : v));
    };
    save();
    return newVisit;
  }, [clients]);
  const endVisit = useCallback(async (visitId: string, visitData: Partial<Visit>) => {
    const now = new Date();
    const visit = visits.find(v => v.id === visitId);
    const horaLlegada = visit?.horaLlegada || now;
    const tiempoTotal = Math.round((now.getTime() - horaLlegada.getTime()) / 60000);
    const location = await getCurrentLocation();
    const {
      data
    } = await supabaseAdmin.from('visits').update({
      cobro: visitData.cobro,
      venta: visitData.venta,
      comentarios: visitData.comentarios || '',
      motivo_no_pago: visitData.motivoNoPago || '',
      productos_vencidos: visitData.productosVencidos ?? null,
      devolucion_items: visitData.devolucionItems || [],
      proxima_visita: visitData.proximaVisita?.toISOString() || null,
      hora_salida: now.toISOString(),
      tiempo_total: tiempoTotal,
      estado: 'completada',
      ubicacion_salida: location ? {
        lat: location.lat,
        lng: location.lng,
        timestamp: location.timestamp.toISOString()
      } : null
    }).eq('id', visitId).select().single();
    if (data) setVisits(prev => prev.map(v => v.id === visitId ? mapVisit(data) : v));
  }, [visits]);
  const getVisitsByVendedor = useCallback((vendedorId: string) => visits.filter(v => v.vendedorId === vendedorId), [visits]);
  const getVisitsByDate = useCallback((date: Date) => visits.filter(v => new Date(v.fecha).toDateString() === date.toDateString()), [visits]);
  const getTodayStats = useCallback((vendedorId?: string) => {
    const today = new Date();
    let todayVisits = visits.filter(v => new Date(v.fecha).toDateString() === today.toDateString());
    if (vendedorId) todayVisits = todayVisits.filter(v => v.vendedorId === vendedorId);
    const completedVisits = todayVisits.filter(v => v.estado === 'completada');
    const totalCobrado = todayVisits.reduce((sum, v) => sum + v.cobro.montoCobrado, 0);
    const totalVendido = todayVisits.reduce((sum, v) => sum + v.venta.montoVenta, 0);
    const tiemposValidos = completedVisits.filter(v => v.tiempoTotal !== null);
    const tiempoPromedio = tiemposValidos.length > 0 ? tiemposValidos.reduce((sum, v) => sum + (v.tiempoTotal || 0), 0) / tiemposValidos.length : 0;
    return {
      totalVisitas: todayVisits.length,
      totalCobrado,
      totalVendido,
      tiempoPromedio: Math.round(tiempoPromedio),
      visitasCompletadas: completedVisits.length,
      visitasEnProgreso: todayVisits.filter(v => v.estado === 'en_progreso').length
    };
  }, [visits]);
  const addClient = useCallback(async (clientData: Omit<Client, 'id'>) => {
    try {
      const {
        data,
        error
      } = await supabaseAdmin.from('clients').insert({
        codigo: clientData.codigo || '',
        nombre: clientData.nombre,
        telefono: clientData.telefono,
        ciudad: clientData.ciudad || '',
        limite: clientData.limite || 0,
        cedula: clientData.cedula || '',
        rnc: clientData.rnc || '',
        ruta: clientData.ruta || '',
        direccion: clientData.direccion,
        vendedor_id: clientData.vendedorId || null,
        vendedor_nombre: clientData.vendedorNombre || null,
        dependencia: clientData.dependencia || '',
        zona: clientData.zona || '',
        lat: clientData.lat || null,
        lng: clientData.lng || null
      }).select().single();
      if (error) return {
        success: false,
        error: error.message
      };
      if (data) setClients(prev => [...prev, mapClient(data)].sort((a, b) => a.nombre.localeCompare(b.nombre)));
      return {
        success: true
      };
    } catch (err: any) {
      return {
        success: false,
        error: err.message
      };
    }
  }, []);
  const updateClient = useCallback(async (id: string, updates: Partial<Client>) => {
    try {
      const dbUpdates: any = {};
      if (updates.codigo !== undefined) dbUpdates.codigo = updates.codigo;
      if (updates.nombre !== undefined) dbUpdates.nombre = updates.nombre;
      if (updates.telefono !== undefined) dbUpdates.telefono = updates.telefono;
      if (updates.ciudad !== undefined) dbUpdates.ciudad = updates.ciudad;
      if (updates.limite !== undefined) dbUpdates.limite = updates.limite;
      if (updates.cedula !== undefined) dbUpdates.cedula = updates.cedula;
      if (updates.rnc !== undefined) dbUpdates.rnc = updates.rnc;
      if (updates.ruta !== undefined) dbUpdates.ruta = updates.ruta;
      if (updates.direccion !== undefined) dbUpdates.direccion = updates.direccion;
      if (updates.vendedorId !== undefined) dbUpdates.vendedor_id = updates.vendedorId;
      if (updates.vendedorNombre !== undefined) dbUpdates.vendedor_nombre = updates.vendedorNombre;
      if (updates.dependencia !== undefined) dbUpdates.dependencia = updates.dependencia;
      if (updates.zona !== undefined) dbUpdates.zona = updates.zona;
      if (updates.lat !== undefined) dbUpdates.lat = updates.lat;
      if (updates.lng !== undefined) dbUpdates.lng = updates.lng;
      const {
        data,
        error
      } = await supabaseAdmin.from('clients').update(dbUpdates).eq('id', id).select().single();
      if (error) return {
        success: false,
        error: error.message
      };
      if (data) setClients(prev => prev.map(c => c.id === id ? mapClient(data) : c));
      return {
        success: true
      };
    } catch (err: any) {
      return {
        success: false,
        error: err.message
      };
    }
  }, []);
  const deleteClient = useCallback(async (id: string) => {
    try {
      const {
        error
      } = await supabaseAdmin.from('clients').delete().eq('id', id);
      if (error) return {
        success: false,
        error: error.message
      };
      setClients(prev => prev.filter(c => c.id !== id));
      return {
        success: true
      };
    } catch (err: any) {
      return {
        success: false,
        error: err.message
      };
    }
  }, []);
  const addUser = useCallback(async (userData: Omit<User, 'id' | 'fechaCreacion' | 'rolNombre' | 'permisos'>) => {
    try {
      // Buscar nombre del rol para la columna legacy "rol"
      const matchedRole = roles.find(r => r.id === userData.rolId);
      const insertData: any = {
        nombre: userData.nombre,
        password: userData.password,
        rol_id: userData.rolId,
        rol: matchedRole?.nombre || 'vendedor',
        activo: userData.activo
      };
      if (userData.email) insertData.email = userData.email.trim().toLowerCase();
      const {
        data,
        error
      } = await supabaseAdmin.from('profiles').insert(insertData).select().single();
      if (error) return {
        success: false,
        error: error.message
      };
      if (data) await loadData();
      return {
        success: true
      };
    } catch (err: any) {
      return {
        success: false,
        error: err.message
      };
    }
  }, [roles]);
  const updateUser = useCallback(async (id: string, updates: Partial<User>) => {
    try {
      const dbUpdates: any = {};
      if (updates.nombre !== undefined) dbUpdates.nombre = updates.nombre;
      if (updates.email !== undefined) dbUpdates.email = updates.email?.trim().toLowerCase() || null;
      if (updates.password !== undefined) dbUpdates.password = updates.password;
      if (updates.rolId !== undefined) {
        dbUpdates.rol_id = updates.rolId;
        const matchedRole = roles.find(r => r.id === updates.rolId);
        if (matchedRole) dbUpdates.rol = matchedRole.nombre;
      }
      if (updates.activo !== undefined) dbUpdates.activo = updates.activo;
      const {
        error
      } = await supabaseAdmin.from('profiles').update(dbUpdates).eq('id', id);
      if (error) {
        if (error.message.includes('email')) {
          delete dbUpdates.email;
          const {
            error: error2
          } = await supabaseAdmin.from('profiles').update(dbUpdates).eq('id', id);
          if (error2) return {
            success: false,
            error: error2.message
          };
          await loadData();
          return {
            success: true
          };
        }
        return {
          success: false,
          error: error.message
        };
      }
      await loadData();
      return {
        success: true
      };
    } catch (err: any) {
      return {
        success: false,
        error: err.message
      };
    }
  }, [roles]);
  const deleteUser = useCallback(async (id: string) => {
    try {
      const {
        error
      } = await supabaseAdmin.from('profiles').delete().eq('id', id);
      if (error) return {
        success: false,
        error: error.message
      };
      setUsers(prev => prev.filter(u => u.id !== id));
      return {
        success: true
      };
    } catch (err: any) {
      return {
        success: false,
        error: err.message
      };
    }
  }, []);
  const addRole = useCallback(async (role: Omit<Role, 'id' | 'fechaCreacion'>) => {
    try {
      const {
        data,
        error
      } = await supabaseAdmin.from('roles').insert({
        nombre: role.nombre,
        permisos: role.permisos
      }).select().single();
      if (error) return {
        success: false,
        error: error.message
      };
      if (data) setRoles(prev => [...prev, mapRole(data)]);
      return {
        success: true
      };
    } catch (err: any) {
      return {
        success: false,
        error: err.message
      };
    }
  }, []);
  const updateRole = useCallback(async (id: string, updates: Partial<Role>) => {
    try {
      const dbUpdates: any = {};
      if (updates.nombre) dbUpdates.nombre = updates.nombre;
      if (updates.permisos) dbUpdates.permisos = updates.permisos;
      const {
        error
      } = await supabaseAdmin.from('roles').update(dbUpdates).eq('id', id);
      if (error) return {
        success: false,
        error: error.message
      };
      setRoles(prev => prev.map(r => r.id === id ? {
        ...r,
        ...updates
      } : r));
      await loadData();
      return {
        success: true
      };
    } catch (err: any) {
      return {
        success: false,
        error: err.message
      };
    }
  }, []);
  const deleteRole = useCallback(async (id: string) => {
    try {
      const {
        error
      } = await supabaseAdmin.from('roles').delete().eq('id', id);
      if (error) return {
        success: false,
        error: error.message
      };
      setRoles(prev => prev.filter(r => r.id !== id));
      return {
        success: true
      };
    } catch (err: any) {
      return {
        success: false,
        error: err.message
      };
    }
  }, []);
  // ── Optimistic order status update (instant UI, then DB) ───────────────────
  const updateOrderStatus = useCallback(async (orderId: string, newStatus: Order['estado']) => {
    // 1. Optimistic: update local state immediately
    setOrders(prev => prev.map(o => o.id === orderId ? {
      ...o,
      estado: newStatus
    } : o));
    // 2. Persist to DB (no await on refreshData — realtime handles sync)
    await supabaseAdmin.from('orders').update({
      estado: newStatus
    }).eq('id', orderId);
  }, []);
  return <AppContext.Provider value={{
    visits,
    clients,
    users,
    roles,
    orders,
    products,
    isLoading,
    newOrderAlert,
    clearNewOrderAlert,
    addVisit,
    updateVisit,
    startVisit,
    endVisit,
    getVisitsByVendedor,
    getVisitsByDate,
    getTodayStats,
    addClient,
    updateClient,
    deleteClient,
    addUser,
    updateUser,
    deleteUser,
    addRole,
    updateRole,
    deleteRole,
    updateOrderStatus,
    getCurrentLocation,
    refreshData: loadData,
    loadProducts
  }} data-id="element-296">
      {children}
    </AppContext.Provider>;
}
export function useApp() {
  const context = useContext(AppContext);
  if (context === undefined) throw new Error('useApp debe usarse dentro de AppProvider');
  return context;
}